import React from 'react'
import './floorSheetReport.css'
import { palette, typography, floorSheetCanvasFrame } from './pqrTheme'
import type { FloorSheetPreviewData } from '../../../utils/pqrPreviewClient'
import {
  formatMisfireDualValue,
  MISFIRE_META_FIELDS,
  MISFIRE_PARAM_ROWS,
  MISFIRE_POSITION_LABELS,
  MISFIRE_POSITIONS,
  MISFIRE_SIDES,
  misfireMetaFieldLabel,
  misfireParamRowLabel
} from '../../../utils/floorSheetCrcFields'
import {
  BEVEL_MEASUREMENT_ROWS,
  bevelRowColWidth,
  clockColWidth,
  DEPOSITION_TEMPLATE_ROWS,
  FS_BEVEL_LABEL_COL,
  FS_BEVEL_PUP_COL,
  FS_CLOCK_ROW_HEIGHT,
  FS_COL_GAP,
  FS_COL_LEFT,
  FS_COL_RIGHT,
  FS_CONTENT_WIDTH,
  FS_EXTRA_COL,
  FS_KV_ROW_HEIGHT,
  FS_LOGO_H,
  FS_LOGO_W,
  FS_PAGE,
  FS_PASS_COL,
  FS_ROW_HEIGHT,
  FS_SECTION_HEADER_HEIGHT,
  FS_TOP_INFO_W,
  headCellUsed,
  IWM_PARAM_FIELDS,
  IWM_PARAM_LABELS,
  iwmColumnWidths,
  kvLabelWidth,
  mergeDepositionTemplateRows,
  mergePreheatTemplateRows,
  PREHEAT_TEMPLATE_ROWS,
  visibleIwmHeads,
  visiblePassRows
} from './floorSheetLayout'
import PqrPreviewFieldLink, {
  type PqrPreviewFieldLinkActions
} from './PqrPreviewFieldLink'
import type { PqrFieldLinkContext } from './pqrFieldLinkRegistry'
import {
  DEPOSITION_TAB,
  MISCELLANEOUS_TAB,
  ROOT_BEAD_TAB,
  ROOT_MISFIRE_TAB
} from '../FloorSheetCrcTabs'

/** Match FloorSheet.tsx CRC tab labels (not exported from that module). */
const BEVEL_MEASUREMENTS_TAB = 'Bevel Measurements'
const PREHEAT_INTERPASS_TAB = 'Preheat / Interpass'

interface Props {
  data: FloorSheetPreviewData
  projectName: string
  isRecordedByDirty?: boolean
  isReviewedByDirty?: boolean
  onSignatoriesClick?: () => void
  fieldLinkContext?: PqrFieldLinkContext
  fieldLinkActions?: PqrPreviewFieldLinkActions
}

const BORDER = `1px solid ${palette.border}`
const HAIRLINE = `0.5px solid ${palette.border}`
/** Darker than unused fill so vertical grid lines stay visible on empty rows. */
const GRID_LINE = '#9ca3af'
const GRID_HAIRLINE = `0.5px solid ${GRID_LINE}`
const GREY_HEADER_BG = '#f2f2f2'
/** Lighter than GRID_LINE / palette.border so empty-row borders remain visible. */
const UNUSED_BG = '#e5e7eb'

const baseText: React.CSSProperties = {
  fontFamily: typography.family,
  color: palette.text,
  fontSize: typography.micro,
  lineHeight: `${FS_ROW_HEIGHT}px`
}

const thStyle: React.CSSProperties = {
  backgroundColor: GREY_HEADER_BG,
  fontSize: typography.micro,
  fontWeight: 700,
  height: FS_CLOCK_ROW_HEIGHT,
  lineHeight: `${FS_CLOCK_ROW_HEIGHT}px`,
  border: GRID_HAIRLINE,
  textAlign: 'center'
}

const tdStyle = (h = FS_ROW_HEIGHT): React.CSSProperties => ({
  fontSize: typography.micro,
  height: h,
  lineHeight: `${h}px`,
  border: GRID_HAIRLINE,
  textAlign: 'center'
})

const tdLabelStyle = (h = FS_ROW_HEIGHT): React.CSSProperties => ({
  ...tdStyle(h),
  textAlign: 'left',
  fontWeight: 700,
  backgroundColor: GREY_HEADER_BG,
  paddingLeft: 2
})

const unusedStyle: React.CSSProperties = {
  backgroundColor: UNUSED_BG,
  border: GRID_HAIRLINE
}

function formatGeneratedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(
    d.getUTCHours()
  )}:${pad(d.getUTCMinutes())}`
}

function formatClockHeader(loc: string): string {
  return loc.replace(" o'clock", '').replace(':00', '')
}

function displayValue(value: string | undefined | null): string {
  const v = (value ?? '').trim()
  return v && v !== '—' ? v : '—'
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div
      style={{
        backgroundColor: palette.banner,
        color: '#ffffff',
        fontSize: typography.sectionHeader,
        fontWeight: 700,
        height: FS_SECTION_HEADER_HEIGHT,
        lineHeight: `${FS_SECTION_HEADER_HEIGHT}px`,
        padding: '0 4px',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        fontFamily: typography.family
      }}
    >
      {title}
    </div>
  )
}

function Kv({ label, value, width }: { label: string; value: string; width: number }) {
  const labelW = kvLabelWidth(width)
  return (
    <div
      style={{
        display: 'flex',
        width,
        minHeight: FS_KV_ROW_HEIGHT,
        border: HAIRLINE,
        borderTop: 'none',
        boxSizing: 'border-box',
        ...baseText
      }}
    >
      <div
        style={{
          ...tdLabelStyle(FS_KV_ROW_HEIGHT),
          width: labelW,
          minWidth: labelW,
          flexShrink: 0,
          height: 'auto',
          minHeight: FS_KV_ROW_HEIGHT,
          whiteSpace: 'normal',
          lineHeight: '13px',
          paddingTop: 1,
          paddingBottom: 1
        }}
      >
        {label}
      </div>
      <div
        style={{
          ...tdStyle(FS_KV_ROW_HEIGHT),
          flex: 1,
          minWidth: 0,
          height: 'auto',
          minHeight: FS_KV_ROW_HEIGHT,
          textAlign: 'left',
          paddingLeft: 2,
          whiteSpace: 'normal',
          lineHeight: '13px',
          paddingTop: 1,
          paddingBottom: 1
        }}
      >
        {displayValue(value)}
      </div>
    </div>
  )
}

function SubsectionLabel({ title, width }: { title: string; width: number }) {
  return (
    <div
      style={{
        ...tdLabelStyle(FS_KV_ROW_HEIGHT),
        width,
        border: HAIRLINE,
        borderTop: 'none',
        marginTop: 2
      }}
    >
      {title}
    </div>
  )
}

function IwmSymmetricalGrid({
  heads,
  tableWidth
}: {
  heads: FloorSheetPreviewData['iwmHeads']
  tableWidth: number
}) {
  const cols = iwmColumnWidths(tableWidth)
  // separate + spacing 0 keeps hairlines visible when unused cells share a grey fill
  const labelCell = (extra?: React.CSSProperties): React.CSSProperties => ({
    ...tdLabelStyle(),
    textAlign: 'center',
    paddingLeft: 0,
    paddingRight: 0,
    ...extra
  })
  const valueCell = (extra?: React.CSSProperties): React.CSSProperties => ({
    ...tdStyle(),
    textAlign: 'center',
    ...extra
  })
  const unusedCell: React.CSSProperties = {
    backgroundColor: UNUSED_BG,
    border: GRID_HAIRLINE
  }

  return (
    <table
      className="fs-report-table"
      style={{
        width: tableWidth,
        tableLayout: 'fixed',
        borderCollapse: 'separate',
        borderSpacing: 0,
        ...baseText
      }}
    >
      <colgroup>
        <col style={{ width: cols.ccwLabel }} />
        <col style={{ width: cols.ccwVal }} />
        <col style={{ width: cols.head }} />
        <col style={{ width: cols.cwVal }} />
        <col style={{ width: cols.cwLabel }} />
      </colgroup>
      <thead>
        <tr>
          <th style={thStyle} colSpan={2}>
            CCW Side
          </th>
          <th style={{ ...thStyle, overflow: 'hidden', whiteSpace: 'nowrap' }}>Head Number</th>
          <th style={thStyle} colSpan={2}>
            CW Side
          </th>
        </tr>
      </thead>
      <tbody>
        {visibleIwmHeads(heads).map((h) => {
          const unused = !headCellUsed(h.cw) && !headCellUsed(h.ccw)
          const fill = unused ? unusedCell : undefined
          return IWM_PARAM_FIELDS.map((field, rowIdx) => (
            <tr key={`${h.head}-${field}`}>
              <td style={labelCell(fill)}>{IWM_PARAM_LABELS[field]}</td>
              <td style={valueCell(fill)}>{h.ccw[field] || '—'}</td>
              {rowIdx === 0 ? (
                <td
                  style={{
                    ...valueCell(fill),
                    verticalAlign: 'middle',
                    fontWeight: 700
                  }}
                  rowSpan={4}
                >
                  {h.head}
                </td>
              ) : null}
              <td style={valueCell(fill)}>{h.cw[field] || '—'}</td>
              <td style={labelCell(fill)}>{IWM_PARAM_LABELS[field]}</td>
            </tr>
          ))
        })}
      </tbody>
    </table>
  )
}

function MisfirePaperGrid({
  misfire,
  tableWidth
}: {
  misfire: FloorSheetPreviewData['misfire']
  tableWidth: number
}) {
  const sideColW = Math.floor(tableWidth * 0.1)
  const paramColW = Math.floor(tableWidth * 0.22)
  const posColW = Math.floor((tableWidth - sideColW - paramColW) / 3)

  return (
    <div style={{ width: tableWidth, ...baseText }}>
      <table className="fs-report-table" style={{ width: tableWidth, ...baseText }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: sideColW, textAlign: 'left' }}>Side</th>
            <th style={{ ...thStyle, width: paramColW, textAlign: 'left' }} />
            {MISFIRE_POSITIONS.map((pos) => (
              <th key={pos} style={{ ...thStyle, width: posColW }}>
                {MISFIRE_POSITION_LABELS[pos]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {MISFIRE_SIDES.map((side) =>
            MISFIRE_PARAM_ROWS.map((row, rowIndex) => (
              <tr key={`${side}-${row.id}`}>
                {rowIndex === 0 ? (
                  <td
                    style={{ ...tdLabelStyle(), width: sideColW, verticalAlign: 'middle' }}
                    rowSpan={MISFIRE_PARAM_ROWS.length}
                  >
                    {side}
                  </td>
                ) : null}
                <td style={{ ...tdLabelStyle(), width: paramColW, fontSize: typography.micro }}>
                  {misfireParamRowLabel(row)}
                </td>
                {MISFIRE_POSITIONS.map((pos) => {
                  const cell = misfire.cells[side][pos]
                  const value =
                    row.kind === 'dual'
                      ? formatMisfireDualValue(cell[row.field1], cell[row.field2])
                      : displayValue(cell[row.field])
                  return (
                    <td key={pos} style={{ ...tdStyle(), width: posColW }}>
                      {value}
                    </td>
                  )
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div style={{ marginTop: 2, width: tableWidth }}>
        {MISFIRE_META_FIELDS.map((field) => (
          <Kv
            key={field.key}
            label={misfireMetaFieldLabel(field)}
            value={misfire[field.key as keyof typeof misfire] as string}
            width={tableWidth}
          />
        ))}
      </div>
    </div>
  )
}

function ClockTable({
  tableWidth,
  locations,
  rows,
  extraHeaders,
  compact = true
}: {
  tableWidth: number
  locations: string[]
  rows: Array<{ label: string; values: Record<string, string>; extra?: string[]; unused?: boolean }>
  extraHeaders?: string[]
  compact?: boolean
}) {
  const extras = extraHeaders ?? []
  const clockW = clockColWidth(tableWidth, extras.length)
  const rh = compact ? FS_CLOCK_ROW_HEIGHT : FS_ROW_HEIGHT
  const cell = (s?: React.CSSProperties) => ({ ...tdStyle(rh), ...s })
  const label = (s?: React.CSSProperties) => ({ ...tdLabelStyle(rh), ...s })

  return (
    <table
      className="fs-report-table"
      style={{ width: tableWidth, borderCollapse: 'separate', borderSpacing: 0, ...baseText }}
    >
      <colgroup>
        <col style={{ width: FS_PASS_COL }} />
        {locations.map((loc) => (
          <col key={loc} style={{ width: clockW }} />
        ))}
        {extras.map((h) => (
          <col key={h} style={{ width: FS_EXTRA_COL }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th style={{ ...thStyle, textAlign: 'left' }} />
          {locations.map((loc) => (
            <th key={loc} style={thStyle}>
              {formatClockHeader(loc)}
            </th>
          ))}
          {extras.map((h) => (
            <th key={h} style={thStyle}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td style={label(row.unused ? unusedStyle : undefined)}>{row.label}</td>
            {locations.map((loc) => (
              <td key={loc} style={cell(row.unused ? unusedStyle : undefined)}>
                {row.values[loc] ?? '—'}
              </td>
            ))}
            {(row.extra ?? extras.map(() => '—')).map((v, i) => (
              <td key={`e${i}`} style={cell(row.unused ? unusedStyle : undefined)}>
                {v || '—'}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function BevelMeasurementBlock({
  tableWidth,
  locations,
  pupLabel,
  rows
}: {
  tableWidth: number
  locations: string[]
  pupLabel: string
  rows: Array<{ label: string; values: Record<string, string> }>
}) {
  const clockW = bevelRowColWidth(tableWidth)
  const byLabel = new Map(rows.map((r) => [r.label, r.values]))
  const templateRows = BEVEL_MEASUREMENT_ROWS.map((label) => ({
    label,
    values: byLabel.get(label) ?? Object.fromEntries(locations.map((l) => [l, '—']))
  }))

  return (
    <table
      className="fs-report-table"
      style={{ width: tableWidth, marginBottom: 2, tableLayout: 'fixed', ...baseText }}
    >
      <colgroup>
        <col style={{ width: FS_BEVEL_LABEL_COL }} />
        {locations.map((loc) => (
          <col key={loc} style={{ width: clockW }} />
        ))}
        <col style={{ width: FS_BEVEL_PUP_COL }} />
      </colgroup>
      <thead>
        <tr>
          <th style={thStyle} />
          {locations.map((loc) => (
            <th key={loc} style={thStyle}>
              {formatClockHeader(loc)}
            </th>
          ))}
          <th style={thStyle}>Pup #</th>
        </tr>
      </thead>
      <tbody>
        {templateRows.map((row, idx) => (
          <tr key={row.label}>
            <td style={tdLabelStyle()}>{row.label}</td>
            {locations.map((loc) => (
              <td key={loc} style={tdStyle()}>
                {row.values[loc] ?? '—'}
              </td>
            ))}
            {idx === 0 ? (
              <td style={{ ...tdStyle(), verticalAlign: 'middle' }} rowSpan={4}>
                {pupLabel || '—'}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function TopBand({ data }: { data: FloorSheetPreviewData }) {
  // Match PQR TopBand: orange bottom rule, orange-left identifier badge,
  // muted labels, uppercase title, identifier / Rev / Sheet order.
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: FS_CONTENT_WIDTH,
        borderBottom: `2px solid ${palette.banner}`,
        paddingBottom: 4,
        marginBottom: 4,
        gap: 8
      }}
      data-testid="floorsheet-preview-top-band"
    >
      <div style={{ width: FS_LOGO_W, height: FS_LOGO_H, flexShrink: 0 }}>
        <img
          src="/crc-logo.png"
          alt="CRC Evans"
          style={{ width: FS_LOGO_W, height: FS_LOGO_H, objectFit: 'contain', display: 'block' }}
        />
      </div>
      <div style={{ flexGrow: 1, textAlign: 'center', minWidth: 0 }}>
        <p
          style={{
            ...baseText,
            fontSize: typography.title,
            fontWeight: 700,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            color: palette.text,
            margin: 0
          }}
        >
          Procedure Qualification Floor Sheet
        </p>
      </div>
      <div
        style={{
          width: FS_TOP_INFO_W,
          minWidth: FS_TOP_INFO_W,
          border: HAIRLINE,
          borderLeft: `3px solid ${palette.banner}`,
          padding: 4,
          flexShrink: 0
        }}
      >
        <TopInfoRow label="PQR No." value={data.pqrNumber} />
        <TopInfoRow label="Rev." value={data.revision} />
        <TopInfoRow label="Sheet" value={data.sheetOf} />
      </div>
    </div>
  )
}

function FloorSheetSignatoryCard({
  label,
  slot,
  width,
  flex,
  isDirty,
  onClick,
  testId
}: {
  label: string
  slot: { name: string; title?: string; date?: string; signatureImage?: string }
  width?: number
  flex?: boolean
  isDirty?: boolean
  onClick?: () => void
  testId: string
}) {
  const today = new Date().toISOString().slice(0, 10)
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `Edit ${label} signatory` : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      style={{
        width: flex ? undefined : width,
        flex: flex ? 1 : undefined,
        minWidth: 0,
        border: HAIRLINE,
        borderLeft: `3px solid ${palette.banner}`,
        padding: 6,
        boxSizing: 'border-box',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: isDirty ? `inset 4px 0 0 0 ${palette.dirty}` : 'none',
        backgroundColor: isDirty ? palette.dirtySurface : 'transparent'
      }}
      data-testid={testId}
    >
      <p
        style={{
          fontSize: typography.caption,
          color: palette.banner,
          fontWeight: 700,
          textTransform: 'uppercase',
          borderBottom: `0.5px solid ${palette.border}`,
          paddingBottom: 2,
          marginBottom: 4,
          marginTop: 0
        }}
      >
        {label}
      </p>
      <div
        style={{
          minHeight: 48,
          border: HAIRLINE,
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 4
        }}
      >
        {slot.signatureImage ? (
          <img
            src={slot.signatureImage}
            alt={`${label} signature`}
            style={{ maxHeight: 44, maxWidth: '100%', objectFit: 'contain' }}
          />
        ) : null}
      </div>
      <div style={{ display: 'flex', fontSize: typography.micro, marginBottom: 2 }}>
        <span style={{ fontWeight: 700, width: 36 }}>Name:</span>
        <span>{slot.name?.trim() || '—'}</span>
      </div>
      <div style={{ display: 'flex', fontSize: typography.micro, marginBottom: 2 }}>
        <span style={{ fontWeight: 700, width: 36 }}>Title:</span>
        <span>{slot.title?.trim() || '—'}</span>
      </div>
      <div style={{ display: 'flex', fontSize: typography.micro }}>
        <span style={{ fontWeight: 700, width: 36 }}>Date:</span>
        <span>{slot.date?.trim() || today}</span>
      </div>
    </div>
  )
}

function TopInfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: typography.micro,
        padding: '0.5px 0',
        fontFamily: typography.family
      }}
    >
      <span style={{ color: palette.textMuted, fontWeight: 700 }}>{label}</span>
      <span style={{ color: palette.text, fontWeight: 700 }}>{value || '—'}</span>
    </div>
  )
}

export default function FloorSheetPreviewCanvas({
  data,
  projectName,
  isRecordedByDirty,
  isReviewedByDirty,
  onSignatoriesClick,
  fieldLinkContext,
  fieldLinkActions
}: Props) {
  const preheatRows = visiblePassRows(
    mergePreheatTemplateRows(PREHEAT_TEMPLATE_ROWS, data.preheat.rows, data.locations)
  )
  const depositionRows = visiblePassRows(
    mergeDepositionTemplateRows(DEPOSITION_TEMPLATE_ROWS, data.deposition.byPass, data.locations)
  )
  const bevelRowsByPupOne = data.bevel.rows.map((r) => ({ label: r.label, values: r.pupOne }))
  const bevelRowsByPupTwo = data.bevel.rows.map((r) => ({ label: r.label, values: r.pupTwo }))

  const linkContext: PqrFieldLinkContext | undefined = fieldLinkContext
    ? {
        ...fieldLinkContext,
        weldId: data.weldId || fieldLinkContext.weldId
      }
    : undefined

  const wrapSection = (
    tabKey: string,
    fieldLabel: string,
    children: React.ReactNode,
    testId: string
  ) => {
    if (!linkContext) {
      return (
        <div data-testid={testId} style={{ cursor: 'default' }}>
          {children}
        </div>
      )
    }
    return (
      <PqrPreviewFieldLink
        descriptor={{
          section: 'floorsheet-section',
          tabKey,
          weldId: data.weldId || undefined
        }}
        fieldLabel={fieldLabel}
        context={linkContext}
        actions={fieldLinkActions}
        dataAttributes={{ 'data-testid': testId }}
        style={{ display: 'block' }}
      >
        {children}
      </PqrPreviewFieldLink>
    )
  }

  const rootBeadTab = data.hasRootMisfirePass ? ROOT_MISFIRE_TAB : ROOT_BEAD_TAB
  const rootBeadLabel = data.hasRootMisfirePass
    ? 'Semi-Auto GMAW Root Misfire Weld Data'
    : 'Internal Root Bead Parameter Record Sheet'

  return (
    <div className="pqr-floorsheet-viewport" data-testid="floorsheet-preview-canvas">
      <div
        className="pqr-report-page"
        data-testid="floorsheet-preview-page-shell"
        style={{
          width: floorSheetCanvasFrame.width,
          maxWidth: floorSheetCanvasFrame.maxWidth,
          minHeight: floorSheetCanvasFrame.minHeight,
          margin: '0 auto',
          backgroundColor: palette.surface,
          border: BORDER,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          padding: `${FS_PAGE.paddingTop}px ${FS_PAGE.paddingH}px ${FS_PAGE.paddingBottom}px`,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          ...baseText
        }}
      >
        <TopBand data={data} />

        {/* Upper band */}
        <div style={{ display: 'flex', gap: FS_COL_GAP, width: FS_CONTENT_WIDTH, marginBottom: 4 }}>
          <div style={{ width: FS_COL_LEFT, flexShrink: 0 }}>
            {wrapSection(
              rootBeadTab,
              rootBeadLabel,
              data.hasRootMisfirePass ? (
                <>
                  <SectionHeader title="Semi-Auto GMAW Root Misfire Weld Data" />
                  <MisfirePaperGrid misfire={data.misfire} tableWidth={FS_COL_LEFT} />
                </>
              ) : (
                <>
                  <SectionHeader title="Internal Root Bead Parameter Record Sheet" />
                  <IwmSymmetricalGrid heads={data.iwmHeads} tableWidth={FS_COL_LEFT} />
                  <Kv
                    label="IWM Travel Speed (in/min)"
                    value={data.targets.travelSpeed || '—'}
                    width={FS_COL_LEFT}
                  />
                </>
              ),
              'floorsheet-preview-section-root-bead'
            )}
            <div style={{ marginTop: 6 }}>
              <SectionHeader title="Target Values" />
            </div>
            <div style={{ width: FS_COL_LEFT, borderBottom: GRID_HAIRLINE }}>
              {wrapSection(
                MISCELLANEOUS_TAB,
                'Miscellaneous',
                <>
                  <Kv
                    label="Maximum Joint Misalignment"
                    value={data.miscellaneous.maxJointMisalignment}
                    width={FS_COL_LEFT}
                  />
                  <SubsectionLabel title="Top of Bevel measurements (in)" width={FS_COL_LEFT} />
                  <Kv
                    label="After Root pass"
                    value={data.miscellaneous.afterRootAvg}
                    width={FS_COL_LEFT}
                  />
                  <Kv
                    label="After Caps Pass"
                    value={data.miscellaneous.afterCapsAvg}
                    width={FS_COL_LEFT}
                  />
                </>,
                'floorsheet-preview-section-miscellaneous'
              )}
            </div>
          </div>

          <div style={{ width: FS_COL_RIGHT, flexShrink: 0 }}>
            {wrapSection(
              PREHEAT_INTERPASS_TAB,
              `Preheat / Interpass Temperature (${data.preheat.unit})`,
              <>
                <SectionHeader title={`Preheat / Interpass Temperature (${data.preheat.unit})`} />
                <ClockTable
                  tableWidth={FS_COL_RIGHT}
                  locations={data.locations}
                  extraHeaders={['Min', 'Max']}
                  rows={preheatRows}
                />
              </>,
              'floorsheet-preview-section-preheat'
            )}
            {wrapSection(
              DEPOSITION_TAB,
              `Deposition Data, Depth from OD (${data.deposition.unit})`,
              <>
                <div style={{ marginTop: 6 }}>
                  <SectionHeader title={`Deposition Data, Depth from OD (${data.deposition.unit})`} />
                </div>
                <ClockTable
                  tableWidth={FS_COL_RIGHT}
                  locations={data.locations}
                  extraHeaders={['Avg']}
                  rows={depositionRows}
                />
              </>,
              'floorsheet-preview-section-deposition'
            )}
            {wrapSection(
              BEVEL_MEASUREMENTS_TAB,
              `Bevel Measurements (${data.bevel.unit})`,
              <>
                <div style={{ marginTop: 6 }}>
                  <SectionHeader title={`Bevel Measurements (${data.bevel.unit})`} />
                </div>
                <BevelMeasurementBlock
                  tableWidth={FS_COL_RIGHT}
                  locations={data.locations}
                  pupLabel={data.bevel.pupOne}
                  rows={bevelRowsByPupOne}
                />
                <BevelMeasurementBlock
                  tableWidth={FS_COL_RIGHT}
                  locations={data.locations}
                  pupLabel={data.bevel.pupTwo}
                  rows={bevelRowsByPupTwo}
                />
              </>,
              'floorsheet-preview-section-bevel'
            )}
          </div>
        </div>

        <div style={{ flexGrow: 1, minHeight: 8 }} />
        <div
          style={{
            display: 'flex',
            gap: 6,
            width: FS_CONTENT_WIDTH,
            alignItems: 'stretch'
          }}
          data-testid="floorsheet-signatories-row"
        >
          {(
            data.recordedByList?.length
              ? data.recordedByList
              : [data.recordedBy ?? { name: '' }]
          ).map((slot, idx) => (
            <FloorSheetSignatoryCard
              key={`recorded-by-${idx}-${slot.name || 'empty'}`}
              label="Recorded By"
              slot={slot}
              width={undefined}
              flex
              isDirty={isRecordedByDirty}
              onClick={onSignatoriesClick}
              testId="floorsheet-recorded-by-signature"
            />
          ))}
          <FloorSheetSignatoryCard
            label="Reviewed By"
            slot={data.reviewedBy ?? { name: '' }}
            width={undefined}
            flex
            isDirty={isReviewedByDirty}
            onClick={onSignatoriesClick}
            testId="floorsheet-reviewed-by-signature"
          />
        </div>

        <div
          style={{
            borderTop: HAIRLINE,
            paddingTop: 3,
            display: 'flex',
            justifyContent: 'space-between',
            width: FS_CONTENT_WIDTH,
            fontSize: typography.micro,
            color: palette.textSubtle,
            textTransform: 'uppercase'
          }}
        >
          <span>Project: {projectName || data.projectName || '—'}</span>
          <span>Generated on: {formatGeneratedAt(data.generatedAt)} UTC</span>
        </div>
      </div>
    </div>
  )
}
